import { useState, useEffect } from "react";
import { getApiUrl } from "@/utils/api";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Loader2, Plus, Pencil, Trash2, Eye, Layout, GripVertical, X, FileText, Sparkles, Grid3x3, Package, Copy, MoreVertical, LayoutDashboard, Lock, Landmark, FileCode2, Gavel, BadgePercent, LayoutGrid, Megaphone } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useAutoSave from "@/hooks/useAutoSave";
import { AutoSaveStatus } from "@/components/ui/AutoSaveStatus";
import InstanceQuickEditDialog from "./InstanceQuickEditDialog";

const API = getApiUrl();

const MODULE_TYPES = [
  { value: "header", label: "Header", icon: Grid3x3, description: "Site header with logo and branding" },
  { value: "navigation", label: "Navigation", icon: Grid3x3, description: "Site navigation with menu items and CTA" },
  { value: "footer", label: "Footer", icon: Package, description: "Footer with links and copyright" },
  { value: "text", label: "Text/HTML Content", icon: FileText, description: "Rich text or HTML content block" },
  { value: "hero", label: "Hero Banner", icon: Sparkles, description: "Large banner with CTA" },
  { value: "all_properties", label: "All Properties", icon: Grid3x3, description: "Display all enabled properties" },
  { value: "featured_properties", label: "Featured Properties", icon: Package, description: "Display featured properties" },
  { value: "property_gallery", label: "Property Image Gallery", icon: Package, description: "Manage and display the image gallery of a specific property" },
  { value: "property_article", label: "Property Article", icon: FileText, description: "AI-generated blog articles grounded in your latest listings" },
  { value: "card", label: "Card", icon: Package, description: "25%-image / 75%-content card (manual or URL-filtered properties)" },
  { value: "go_to", label: "Go To", icon: Grid3x3, description: "Up to four CTAs to navigate between pages" },
  { value: "products", label: "Products / Shop", icon: Package, description: "Grid of live products from the catalogue (with Add to basket)" },
  { value: "basket", label: "Basket", icon: Package, description: "Inline basket contents with remove + Checkout CTA" },
  { value: "checkout", label: "Checkout", icon: Package, description: "Customer details form + Stripe payment redirect" },
  { value: "verify_photo", label: "Verify Photo", icon: Sparkles, description: "Upload two photos and get an AI-driven likeness score" },
  { value: "milestones", label: "Property Milestones", icon: LayoutDashboard, description: "Agent-only dashboard to track per-property milestones and progress" },
  { value: "capture_property_form", label: "Capture Property Form", icon: FileText, description: "Public form for unregistered owners to submit a property — captured as an unclaimed lead" },
  { value: "house_prices_tool", label: "House Prices", icon: Landmark, description: "Public postcode → AI price estimate tool + a CTA to any page" },
  { value: "planning_applications_tool", label: "Planning Applications", icon: FileCode2, description: "Public postcode search of live UK planning applications (PlanIt) + a CTA to any page" },
  { value: "planning_appeals_tool", label: "Planning Appeals", icon: Gavel, description: "Public postcode/street search of UK Planning Inspectorate appeal decisions + a CTA to any page" },
  { value: "pricing_table", label: "Pricing Table", icon: BadgePercent, description: "Manageable multi-tier price comparison (name, price, features, CTA per tier)" },
  { value: "cards_grid", label: "Cards Grid", icon: LayoutGrid, description: "Add up to 16 cards (4 per row, 4 rows) — each with a heading, sub-heading, rich text, CTA, background image and its own border style" },
  { value: "advert", label: "Advert", icon: Megaphone, description: "Portrait social-ad graphic — background image (zoom/transparency/blur), a top-left logo badge, and up to 4 rich-text cards positioned above, below or overlapping the image" },
];

// Module types that have a reusable Module Instance picker.
// Every module type is now instance-eligible — the property-listing modules
// also carry a configurable heading + CTA, so they belong here too.
const INSTANCE_MODULE_TYPES = [
  "header",
  "navigation",
  "footer",
  "text",
  "hero",
  "go_to",
  "all_properties",
  "featured_properties",
  "property_gallery",
  "property_article",
  "card",
  "products",
  "basket",
  "checkout",
  "verify_photo",
  "milestones",
  "capture_property_form",
  "house_prices_tool",
  "planning_applications_tool",
  "planning_appeals_tool",
  "pricing_table",
  "cards_grid",
  "advert",
];

// Sortable Module Item Component
function SortableModuleItem({ id, module, onRemove, onUpdateSpan, onEdit, instances }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const moduleType = MODULE_TYPES.find(m => m.value === module.module_type);
  const Icon = moduleType?.icon || FileText;
  const instance = module.instance_id ? instances.find((i) => i.instance_id === module.instance_id) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-4 bg-white border rounded-lg hover:border-primary/50 transition-colors"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </div>

      <div
        className="flex-1 flex items-center gap-2 cursor-pointer rounded-md hover:bg-muted/40 p-1 -m-1"
        onClick={() => onEdit(module)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onEdit(module); } }}
        data-testid={`module-card-body-${module.id}`}
        title="Click to edit this module"
      >
        <Icon className="h-4 w-4 text-primary" />
        <div>
          <p className="font-medium text-sm">
            {instance ? instance.name : (moduleType?.label || "Unknown Module")}
          </p>
          <p className="text-xs text-muted-foreground">
            {moduleType?.label || module.module_type}{instance ? "" : (moduleType?.description ? ` — ${moduleType.description}` : "")}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">Width:</Label>
        <select
          value={module.column_span}
          onChange={(e) => onUpdateSpan(module.id, parseInt(e.target.value))}
          className="h-8 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="1">1/4 (Quarter)</option>
          <option value="2">1/2 (Half)</option>
          <option value="4">Full Width</option>
        </select>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              data-testid={`row-actions-${module.id}`}
              aria-label="Row actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem
              onClick={() => onEdit(module)}
              data-testid={`edit-module-${module.id}`}
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onRemove(module.id)}
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              data-testid={`remove-module-${module.id}`}
            >
              <X className="h-4 w-4 mr-2" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function PageBuilder() {
  const [compositions, setCompositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showBuilderDialog, setShowBuilderDialog] = useState(false);
  const [editingComposition, setEditingComposition] = useState(null);
  const [saving, setSaving] = useState(false);

  const [compositionForm, setCompositionForm] = useState({
    page_slug: "",
    page_title: "",
    enabled: true,
    requires_agent_login: false
  });

  const [builderModules, setBuilderModules] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [editingModule, setEditingModule] = useState(null);
  const [showModuleEditDialog, setShowModuleEditDialog] = useState(false);
  const [moduleEditFields, setModuleEditFields] = useState({});
  const [instances, setInstances] = useState([]);
  // Quick-edit dialog for instance-backed modules — opened by clicking a
  // module card in the builder. Renders the full ModuleConfigFields editor
  // so admins can tweak the underlying instance without leaving the page
  // builder.
  const [quickEditInstanceId, setQuickEditInstanceId] = useState(null);
  const [allPages, setAllPages] = useState({ modular: [], preset: [], custom: [] });

  // Add-module dialog state: user picks an instance directly (its module_type
  // is derived from the chosen instance). Special non-instance modules (like
  // all_properties / featured_properties) appear in a separate option group.
  const [showAddModuleDialog, setShowAddModuleDialog] = useState(false);
  const [pickedAddValue, setPickedAddValue] = useState("");

  // Slug preview modal — shows the public page rendered inside an iframe.
  const [previewSlug, setPreviewSlug] = useState(null);
  const openSlugPreview = (comp) => {
    const slug = comp.page_slug === null ? "" : (comp.page_slug || "");
    setPreviewSlug({ slug, title: comp.page_title });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const [compRes, instRes, navRes, customRes] = await Promise.all([
        axios.get(`${API}/admin/page-compositions`, { withCredentials: true }),
        axios.get(`${API}/admin/module-instances`, { withCredentials: true }),
        axios.get(`${API}/navigation`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/custom-static-pages`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      const sortedCompositions = [...(compRes.data.compositions || [])].sort((a, b) => (a.page_title || "").localeCompare(b.page_title || ""));
      const sortedInstances = [...(instRes.data.instances || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setCompositions(sortedCompositions);
      setInstances(sortedInstances);
      // `allPages` fuels the "Link to page" dropdown that lives inside
      // several ModuleConfigFields branches (e.g. go_to CTAs).
      setAllPages({
        modular: sortedCompositions,
        preset: (navRes.data || []).filter((p) => p.path && p.path !== "#"),
        custom: customRes.data.pages || [],
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (!silent) toast.error("Failed to load data");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const refreshInstances = async () => {
    try {
      const r = await axios.get(`${API}/admin/module-instances`, { withCredentials: true });
      setInstances(r.data.instances || []);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreatePage = () => {
    setEditingComposition(null);
    setCompositionForm({ page_slug: "", page_title: "", enabled: true, requires_agent_login: false });
    setShowDialog(true);
  };

  const handleEditPage = (composition) => {
    setEditingComposition(composition);
    setCompositionForm({
      page_slug: composition.page_slug,
      page_title: composition.page_title,
      enabled: composition.enabled,
      requires_agent_login: composition.requires_agent_login || false
    });
    setShowDialog(true);
  };

  const handleSavePage = async () => {
    if (!compositionForm.page_title.trim()) {
      toast.error("Page title is required");
      return;
    }

    // Normalize slug: empty or "/" means home page (store as null)
    let normalizedSlug = compositionForm.page_slug?.trim();
    if (!normalizedSlug || normalizedSlug === "/" || normalizedSlug === "home") {
      normalizedSlug = null; // Home page
    }

    try {
      setSaving(true);

      if (editingComposition) {
        await axios.put(
          `${API}/admin/page-compositions/${editingComposition.composition_id}`,
          { ...compositionForm, page_slug: normalizedSlug },
          { withCredentials: true }
        );
        toast.success("Page updated successfully");
      } else {
        await axios.post(
          `${API}/admin/page-compositions`,
          { ...compositionForm, page_slug: normalizedSlug, modules: [] },
          { withCredentials: true }
        );
        toast.success("Page created successfully");
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save page:", error);
      toast.error(error.response?.data?.detail || "Failed to save page");
    } finally {
      setSaving(false);
    }
  };

  // Inline table toggles — flip a single flag and PUT immediately so the
  // admin doesn't need to open the edit dialog for a simple on/off switch.
  const handleToggleField = async (comp, field, value) => {
    setCompositions((prev) => prev.map((c) => (
      c.composition_id === comp.composition_id ? { ...c, [field]: value } : c
    )));
    try {
      await axios.put(
        `${API}/admin/page-compositions/${comp.composition_id}`,
        { [field]: value },
        { withCredentials: true }
      );
    } catch (error) {
      console.error(`Failed to toggle ${field}:`, error);
      toast.error(error.response?.data?.detail || "Failed to update page");
      setCompositions((prev) => prev.map((c) => (
        c.composition_id === comp.composition_id ? { ...c, [field]: comp[field] } : c
      )));
    }
  };

  const handleDeletePage = async (compositionId) => {
    if (!confirm("Are you sure you want to delete this page?")) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/page-compositions/${compositionId}`, {
        withCredentials: true
      });
      toast.success("Page deleted successfully");
      fetchData();
    } catch (error) {
      console.error("Failed to delete page:", error);
      toast.error("Failed to delete page");
    }
  };

  const handleDuplicatePage = async (composition) => {
    try {
      // Create a copy with a new title
      const newTitle = `${composition.page_title} (Copy)`;
      const baseSlug = composition.page_slug || `page-${Date.now()}`;
      const newSlug = `${baseSlug}-copy-${Date.now()}`;
      
      // Create the duplicate page
      const duplicateData = {
        page_title: newTitle,
        page_slug: newSlug,
        modules: composition.modules || [],
        enabled: false, // Start as disabled
        requires_agent_login: composition.requires_agent_login || false
      };

      const response = await axios.post(
        `${API}/admin/page-compositions`,
        duplicateData,
        { withCredentials: true }
      );

      toast.success(`Page duplicated as "${newTitle}"`);
      fetchData();
    } catch (error) {
      console.error("Failed to duplicate page:", error);
      toast.error(error.response?.data?.detail || "Failed to duplicate page");
    }
  };

  const handleBuildPage = (composition) => {
    setEditingComposition(composition);
    
    // Set up modules for builder
    const pageModules = composition.modules || [];
    setBuilderModules(pageModules.map((m, idx) => ({
      ...m,
      id: m.id || `module-${idx}-${Date.now()}`, // For drag-and-drop
      order: m.order || idx
    })));

    // Available module types are ALL types — users can add multiple of any
    // type now that each is backed by a named Module Instance.
    setAvailableModules(MODULE_TYPES);

    setShowBuilderDialog(true);
  };

  const openAddModuleDialog = () => {
    setPickedAddValue("");
    setShowAddModuleDialog(true);
  };

  const handleConfirmAddModule = () => {
    if (!pickedAddValue) {
      toast.error("Please choose a module instance");
      return;
    }

    let moduleType = null;
    let instanceId = null;
    if (pickedAddValue.startsWith("type:")) {
      // Special non-instance module type (e.g. all_properties)
      moduleType = pickedAddValue.slice(5);
    } else {
      // Format: "instance:<id>"
      instanceId = pickedAddValue.startsWith("instance:")
        ? pickedAddValue.slice(9)
        : pickedAddValue;
      const inst = instances.find((i) => i.instance_id === instanceId);
      if (!inst) {
        toast.error("Selected instance no longer exists — please refresh");
        return;
      }
      moduleType = inst.module_type;
    }

    const newModule = {
      id: `module-${Date.now()}`,
      module_type: moduleType,
      instance_id: instanceId,
      order: builderModules.length,
      column_span: 4,
      config_override: {},
    };

    setBuilderModules((prev) => [...prev, newModule]);
    setShowAddModuleDialog(false);
  };

  const handleAddModule = (moduleType) => {
    // Quick add for non-instance module types
    const newModule = {
      id: `module-${Date.now()}`,
      module_type: moduleType,
      instance_id: null,
      order: builderModules.length,
      column_span: 4,
      config_override: {}
    };
    setBuilderModules(prev => [...prev, newModule]);
  };

  const handleRemoveModule = (moduleRowId) => {
    setBuilderModules(prev => prev.filter(m => m.id !== moduleRowId));
  };

  const handleUpdateSpan = (moduleRowId, newSpan) => {
    setBuilderModules(prev => prev.map(m =>
      m.id === moduleRowId ? { ...m, column_span: newSpan } : m
    ));
  };

  const handleEditModule = (module) => {
    // Instance-backed modules use the full ModuleConfigFields editor via
    // InstanceQuickEditDialog so admins can tweak every field (not just
    // the tiny set the legacy inline dialog supported). Non-instance
    // "special" modules (all_properties, featured_properties…) fall back
    // to the legacy dialog.
    if (module.instance_id) {
      setQuickEditInstanceId(module.instance_id);
      return;
    }
    setEditingModule(module);
    setModuleEditFields(module.config_override || {});
    setShowModuleEditDialog(true);
  };

  const handleSaveModuleEdit = () => {
    if (!editingModule) return;

    setBuilderModules(prev => prev.map(m =>
      m.id === editingModule.id
        ? { ...m, config_override: moduleEditFields }
        : m
    ));

    setShowModuleEditDialog(false);
    setEditingModule(null);
    setModuleEditFields({});
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setBuilderModules((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSaveBuilder = async () => {
    try {
      setSaving(true);

      // Prepare modules with updated order - using module_type and instance_id
      const modulesToSave = builderModules.map((m, idx) => ({
        module_type: m.module_type,
        instance_id: m.instance_id || null,
        order: idx,
        column_span: m.column_span,
        config_override: m.config_override || {}
      }));

      await axios.put(
        `${API}/admin/page-compositions/${editingComposition.composition_id}`,
        { modules: modulesToSave },
        { withCredentials: true }
      );

      toast.success("Page layout saved successfully");
      setShowBuilderDialog(false);
      fetchData();
    } catch (error) {
      console.error("Failed to save layout:", error);
      toast.error("Failed to save layout");
    } finally {
      setSaving(false);
    }
  };

  // Auto-save wiring — one hook per open dialog. New pages/compositions still
  // use the explicit primary button so the admin locks-in the slug first.
  const pageDialogAutoSave = useAutoSave(
    compositionForm,
    async (form) => {
      let normalizedSlug = form.page_slug?.trim();
      if (!normalizedSlug || normalizedSlug === "/" || normalizedSlug === "home") {
        normalizedSlug = null;
      }
      await axios.put(
        `${API}/admin/page-compositions/${editingComposition.composition_id}`,
        { ...form, page_slug: normalizedSlug },
        { withCredentials: true }
      );
      fetchData({ silent: true });
    },
    {
      enabled: Boolean(showDialog && editingComposition),
      canSave: (form) => Boolean(form?.page_title?.trim()),
    }
  );

  const builderAutoSave = useAutoSave(
    builderModules,
    async (mods) => {
      const modulesToSave = (mods || []).map((m, idx) => ({
        module_type: m.module_type,
        instance_id: m.instance_id || null,
        order: idx,
        column_span: m.column_span,
        config_override: m.config_override || {},
      }));
      await axios.put(
        `${API}/admin/page-compositions/${editingComposition.composition_id}`,
        { modules: modulesToSave },
        { withCredentials: true }
      );
      // Refresh silently — the builder canvas already reflects local state.
      fetchData({ silent: true });
    },
    {
      enabled: Boolean(showBuilderDialog && editingComposition),
      // Only allow the debounced save once the modules array is a real array.
      canSave: (mods) => Array.isArray(mods),
    }
  );



  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Page Builder</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage modular pages
          </p>
        </div>
        <Button onClick={handleCreatePage} className="bg-primary text-white">
          <Plus className="h-4 w-4 mr-2" />
          Create Page
        </Button>
      </div>

      {/* Pages Table */}
      <Card className="p-6">
        {compositions.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
            <Layout className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No pages yet. Create your first modular page to get started.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page Title</TableHead>
                <TableHead className="hidden sm:table-cell">Slug</TableHead>
                <TableHead className="hidden md:table-cell">Modules</TableHead>
                <TableHead className="hidden sm:table-cell">Enabled</TableHead>
                <TableHead className="hidden sm:table-cell">Agent Only</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {compositions.map((comp) => (
                <TableRow key={comp.composition_id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{comp.page_title}</div>
                      <code className="sm:hidden bg-muted px-2 py-0.5 rounded text-xs mt-1 inline-block">
                        {comp.page_slug === null ? "/" : `/${comp.page_slug}`}
                      </code>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <button
                      type="button"
                      onClick={() => openSlugPreview(comp)}
                      data-testid={`preview-slug-${comp.composition_id}`}
                      className="bg-muted hover:bg-primary/10 hover:text-primary px-2 py-1 rounded text-sm font-mono transition-colors cursor-pointer"
                      title="Preview this page"
                    >
                      {comp.page_slug === null ? "/" : `/${comp.page_slug}`}
                    </button>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">{comp.modules?.length || 0} modules</TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Switch
                      checked={comp.enabled}
                      onCheckedChange={(checked) => handleToggleField(comp, "enabled", checked)}
                      data-testid={`page-enabled-toggle-${comp.composition_id}`}
                      aria-label="Enable this page"
                    />
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={Boolean(comp.requires_agent_login)}
                        onCheckedChange={(checked) => handleToggleField(comp, "requires_agent_login", checked)}
                        data-testid={`page-agent-login-toggle-${comp.composition_id}`}
                        aria-label="Require agent login to view this page"
                      />
                      {comp.requires_agent_login && <Lock className="h-3.5 w-3.5 text-amber-600" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            data-testid={`row-actions-${comp.composition_id}`}
                            aria-label="Row actions"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem onClick={() => handleBuildPage(comp)}>
                            <Layout className="h-4 w-4 mr-2" />
                            Build
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditPage(comp)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicatePage(comp);
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDeletePage(comp.composition_id)}
                            className="text-red-600 focus:text-red-700 focus:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create/Edit Page Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingComposition ? "Edit Page" : "Create New Page"}
            </DialogTitle>
            <DialogDescription>
              {editingComposition ? "Update page details" : "Create a new modular page"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Page Title *</Label>
              <Input
                placeholder="e.g., About Us, Services"
                value={compositionForm.page_title}
                onChange={(e) => setCompositionForm(prev => ({ ...prev, page_title: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Copy slug from an existing page (optional)</Label>
              <select
                className="w-full h-9 px-3 border border-input rounded-md text-sm bg-background"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return;
                  const slug = e.target.value === "__home__" ? "" : e.target.value;
                  setCompositionForm(prev => ({ ...prev, page_slug: slug }));
                }}
                data-testid="page-slug-picker"
              >
                <option value="">Pick a page to fill the slug below…</option>
                {(() => {
                  const seenSlugs = new Set();
                  const normalize = (s) => (s || "").trim().toLowerCase().replace(/^\//, "");
                  const modularOptions = allPages.modular.filter((p) => p.composition_id !== editingComposition?.composition_id);
                  modularOptions.forEach((p) => seenSlugs.add(normalize(p.page_slug)));
                  const presetOptions = allPages.preset.filter((p) => {
                    const slug = normalize(p.path);
                    if (seenSlugs.has(slug)) return false;
                    seenSlugs.add(slug);
                    return true;
                  });
                  const customOptions = allPages.custom.filter((p) => {
                    const slug = normalize(p.slug);
                    if (seenSlugs.has(slug)) return false;
                    seenSlugs.add(slug);
                    return true;
                  });
                  return (
                    <>
                      {modularOptions.length > 0 && (
                        <optgroup label="Modular Pages">
                          {modularOptions.map((p) => (
                            <option key={p.composition_id} value={p.page_slug ? normalize(p.page_slug) : "__home__"}>
                              {p.page_title} (/{p.page_slug || ""})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {presetOptions.length > 0 && (
                        <optgroup label="Preset Pages">
                          {presetOptions.map((p) => (
                            <option key={p.nav_id} value={normalize(p.path)}>
                              {p.title} ({p.path})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {customOptions.length > 0 && (
                        <optgroup label="Custom Pages">
                          {customOptions.map((p) => (
                            <option key={p.page_id} value={normalize(p.slug)}>
                              {p.title} (/{p.slug})
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>

            <div className="space-y-2">
              <Label>Page Slug (URL path)</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/</span>
                <Input
                  placeholder="Leave empty for home page, or enter slug like 'about-us'"
                  value={compositionForm.page_slug || ""}
                  onChange={(e) => setCompositionForm(prev => ({
                    ...prev,
                    page_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')
                  }))}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty or use "/" for home page. Otherwise use lowercase letters, numbers, and hyphens.
              </p>
            </div>
          </div>

          <DialogFooter>
            {editingComposition && (
              <AutoSaveStatus
                status={pageDialogAutoSave.status}
                lastSavedAt={pageDialogAutoSave.lastSavedAt}
                className="mr-auto"
              />
            )}
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {editingComposition ? "Close" : "Cancel"}
            </Button>
            {!editingComposition && (
              <Button onClick={handleSavePage} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Create Page"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Page Builder Dialog */}
      <Dialog open={showBuilderDialog} onOpenChange={setShowBuilderDialog}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Build Page: {editingComposition?.page_title}
            </DialogTitle>
            <DialogDescription>
              Drag and drop modules to arrange your page layout
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4 space-y-6">
            {/* Current Modules - Already on Page */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Module Instances</h3>
                  <p className="text-sm text-muted-foreground">Module instances currently included on this page ({builderModules.length})</p>
                </div>
              </div>
              
              {builderModules.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg bg-muted/20">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p className="text-sm font-medium">No modules added yet</p>
                  <p className="text-xs mt-1">Click "Add Module" below to insert one</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={builderModules.map(m => m.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {builderModules.map((module) => (
                        <SortableModuleItem
                          key={module.id}
                          id={module.id}
                          module={module}
                          onRemove={handleRemoveModule}
                          onUpdateSpan={handleUpdateSpan}
                          onEdit={handleEditModule}
                          instances={instances}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>

            {/* Add Module Instance Action */}
            <div className="pt-6 border-t">
              <Button
                onClick={openAddModuleDialog}
                className="w-full"
                variant="outline"
                data-testid="open-add-module-dialog"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Module Instance
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Pick from your library of Module Instances. You can add multiple instances of the same type.
              </p>
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <AutoSaveStatus
              status={builderAutoSave.status}
              lastSavedAt={builderAutoSave.lastSavedAt}
              className="mr-auto"
            />
            <Button variant="outline" onClick={() => setShowBuilderDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slug Preview Modal — renders the public page in an iframe */}
      <Dialog
        open={!!previewSlug}
        onOpenChange={(open) => !open && setPreviewSlug(null)}
      >
        <DialogContent
          className="sm:max-w-[1100px] max-h-[92vh] p-0 overflow-hidden flex flex-col"
          data-testid="slug-preview-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Preview — {previewSlug?.title}
            </DialogTitle>
            <DialogDescription className="flex items-center justify-between gap-2 mt-1">
              <code className="bg-muted px-2 py-0.5 rounded text-xs font-mono">
                /{previewSlug?.slug || ""}
              </code>
              {previewSlug && (
                <a
                  href={`/${previewSlug.slug || ""}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Open in new tab →
                </a>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {previewSlug && (
              <iframe
                key={previewSlug.slug}
                src={`/${previewSlug.slug || ""}`}
                title={`Preview of ${previewSlug.title}`}
                className="w-full h-[75vh] border-0 bg-white"
                data-testid="slug-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Module Instance Dialog */}
      <Dialog open={showAddModuleDialog} onOpenChange={setShowAddModuleDialog}>
        <DialogContent className="sm:max-w-[640px] max-h-[88vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Module Instance</DialogTitle>
            <DialogDescription>
              Choose a Module Instance from your library. Click an item to select, then press <strong>Add</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-5 py-2">
            {MODULE_TYPES
              .filter((t) => INSTANCE_MODULE_TYPES.includes(t.value))
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((t) => {
                const opts = instances.filter((i) => i.module_type === t.value);
                if (opts.length === 0) return null;
                const TypeIcon = t.icon || Package;
                return (
                  <div key={t.value} data-testid={`instance-group-${t.value}`}>
                    <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1">
                      <TypeIcon className="h-4 w-4 text-primary" />
                      <h4 className="font-semibold text-sm">{t.label}</h4>
                      <span className="text-xs text-muted-foreground">({opts.length})</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {opts.map((i) => {
                        const value = `instance:${i.instance_id}`;
                        const selected = pickedAddValue === value;
                        return (
                          <button
                            key={i.instance_id}
                            type="button"
                            onClick={() => setPickedAddValue(value)}
                            data-testid={`pick-instance-${i.instance_id}`}
                            className={`text-left p-3 border rounded-lg transition-all ${
                              selected
                                ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                                : "border-border hover:border-primary/50 hover:bg-muted/30"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex-1">
                                <p className="text-sm font-medium leading-tight">{i.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {i.is_default ? "Default · " : ""}
                                  {(i.updated_at || "").split("T")[0]}
                                </p>
                              </div>
                              {selected && (
                                <span className="text-primary text-xs font-semibold">✓</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

            {/* Special non-instance module types */}
            {(() => {
              const specials = MODULE_TYPES.filter(
                (t) => !INSTANCE_MODULE_TYPES.includes(t.value)
              ).sort((a, b) => a.label.localeCompare(b.label));
              if (specials.length === 0) return null;
              return (
                <div data-testid="instance-group-dynamic">
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <h4 className="font-semibold text-sm">Dynamic content</h4>
                    <span className="text-xs text-muted-foreground">(no instance needed)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {specials.map((t) => {
                      const value = `type:${t.value}`;
                      const selected = pickedAddValue === value;
                      const TypeIcon = t.icon || Package;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          onClick={() => setPickedAddValue(value)}
                          data-testid={`pick-type-${t.value}`}
                          className={`text-left p-3 border rounded-lg transition-all ${
                            selected
                              ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                              : "border-border hover:border-primary/50 hover:bg-muted/30"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <TypeIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-sm font-medium leading-tight">{t.label}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                            </div>
                            {selected && (
                              <span className="text-primary text-xs font-semibold">✓</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {instances.length === 0 && (
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                No module instances exist yet. Open the
                <strong> Module Instances </strong> tab to create one.
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-3 mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={refreshInstances}
              className="text-xs text-primary underline mr-auto"
            >
              Refresh list
            </button>
            <Button variant="outline" onClick={() => setShowAddModuleDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAddModule}
              disabled={!pickedAddValue}
              data-testid="confirm-add-module"
            >
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module Edit Dialog */}
      <Dialog open={showModuleEditDialog} onOpenChange={setShowModuleEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Module: {editingModule && MODULE_TYPES.find(t => t.value === editingModule.module_type)?.label}</DialogTitle>
            <DialogDescription>
              Configure module-specific settings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Header Module Fields */}
            {editingModule?.module_type === "header" && (
              <>
                <div className="space-y-2">
                  <Label>Company Logo URL</Label>
                  <Input
                    placeholder="https://example.com/logo.png"
                    value={moduleEditFields.logo_url || ""}
                    onChange={(e) => setModuleEditFields(prev => ({ ...prev, logo_url: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Login Text</Label>
                  <Input
                    placeholder="Login"
                    value={moduleEditFields.login_text || ""}
                    onChange={(e) => setModuleEditFields(prev => ({ ...prev, login_text: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Login Link</Label>
                  <Input
                    placeholder="/admin/login"
                    value={moduleEditFields.login_link || ""}
                    onChange={(e) => setModuleEditFields(prev => ({ ...prev, login_link: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact CTA Text</Label>
                  <Input
                    placeholder="Contact Us"
                    value={moduleEditFields.contact_text || ""}
                    onChange={(e) => setModuleEditFields(prev => ({ ...prev, contact_text: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contact Link</Label>
                  <Input
                    placeholder="/contact"
                    value={moduleEditFields.contact_link || ""}
                    onChange={(e) => setModuleEditFields(prev => ({ ...prev, contact_link: e.target.value }))}
                  />
                </div>
              </>
            )}

            {/* Generic message for other modules */}
            {editingModule && 
             editingModule.module_type !== "header" && (
              <p className="text-sm text-muted-foreground">
                This module type doesn't have editable fields yet.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModuleEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveModuleEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instance-backed module quick editor — opens when the admin clicks
          a module row in the builder. Saves directly to the shared instance
          so the change surfaces on every page it's used. */}
      <InstanceQuickEditDialog
        open={Boolean(quickEditInstanceId)}
        onOpenChange={(open) => { if (!open) setQuickEditInstanceId(null); }}
        instanceId={quickEditInstanceId}
        onSaved={() => { refreshInstances(); }}
        allPages={allPages}
      />
    </div>
  );
}
